import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Modal } from '@core/enums/modal';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { Permission } from '@core/enums/permissions';
import { IModalEmitData } from '@core/interfaces/imodal';
import { IGlobalAnnouncement, IGlobalAnnouncementSearchRequest } from '@core/interfaces/iglobal-announcement';
import { GlobalAnnouncementService } from '@core/services/global-announcement.service';
import { CustomValidators } from '@core/validators/custom.validator';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';
import { AddGlobalAnnoucementsModalComponent } from './add-global-annoucements-modal/add-global-annoucements-modal.component';
import { Subscription, take } from 'rxjs';
import { asUtc } from '@core/services/helper.service';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-global-announcements',
  templateUrl: './global-announcements.component.html',
  styleUrls: ['./global-announcements.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class GlobalAnnouncementsComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();

  // Search payload
  searchPayload: IGlobalAnnouncementSearchRequest = {};

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  Permission = Permission;

  searchForm!: FormGroup;
  searchStateId: number | null = null;
  resetDates: boolean = false;
  clearDropdowns: boolean = false;
  searchAllStates: boolean = true;

  get startDate() {
    return this.searchForm.get('startDate');
  }

  get endDate() {
    return this.searchForm.get('endDate');
  }

  allAnnouncements: IGlobalAnnouncement[] = [];

  dataSource = new MatTableDataSource<IGlobalAnnouncement>([]);
  tableData: IGlobalAnnouncement[] = [];
  tableColumns: any[] = [];

  pageSizeOptions: number[] = [10, 25, 50];
  pageSize: number = 10;
  pageIndex: number = 0;
  totalData: number = 0;

  constructor(
    private announcementService: GlobalAnnouncementService,
    public dialog: MatDialog,
    private fb: FormBuilder,
    private translocoService: TranslocoService,
  ) { }

    ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.paginator._intl.itemsPerPageLabel = '';
  }

  ngOnInit(): void {
    this.tableColumns = [
      { columnDef: 'stateName', header: this.translocoService.translate('configuration.common.state'), type: 'text' },
      { columnDef: 'message', header: this.translocoService.translate('configuration.common.message'), type: 'text' },
      { columnDef: 'startDate', header: this.translocoService.translate('configuration.common.startDate'), type: 'date' },
      { columnDef: 'endDate', header: this.translocoService.translate('configuration.common.endDate'), type: 'date' },
      { columnDef: 'edit', header: this.translocoService.translate('configuration.common.edit'), type: 'edit' },
      { columnDef: 'delete', header: this.translocoService.translate('configuration.common.delete'), type: 'delete' },
    ];
    this.searchForm = this.fb.group(
      {
        startDate: [''],
        endDate: [''],
      },
      {
        validators: [CustomValidators.dateValidator('startDate', 'endDate')],
      }
    );
  }

  private loadAnnouncements(stateId?: number | null): void {
    this.searchPayload = {
      stateId: stateId ?? null,
      startDate: this.startDate?.value || null,
      endDate: this.endDate?.value || null,
    };

    this.subscriptions.add(
      this.announcementService.search(this.searchPayload)
        .pipe(take(1))
        .subscribe(results => {
          this.allAnnouncements = results.map(announcement => ({
            ...announcement,
            endDate: asUtc(announcement.endDate)
          }));
          this.totalData = this.allAnnouncements.length;
          this.applyPagination();
        })
    );
  }

  private applyPagination(): void {
    const start = this.pageIndex * this.pageSize;
    this.tableData = this.allAnnouncements.slice(start, start + this.pageSize);
    this.dataSource.data = this.tableData;
  }

  onStateChange(event: any): void {
    this.searchStateId = event?.id ?? null;
  }

  onDateChange(field: 'startDate' | 'endDate', date: string): void {
    this[field]?.setValue(date || '');
  }

  onSearch(): void {
    if (this.searchForm.invalid) return;
    this.pageIndex = 0;
    this.loadAnnouncements(this.searchStateId);
  }

  onClear(): void {
    this.searchAllStates = false;
    this.searchStateId = null;
    this.searchForm.reset({ startDate: '', endDate: '' });
    this.resetDates = !this.resetDates;
    this.clearDropdowns = !this.clearDropdowns;
    this.pageIndex = 0;
    this.allAnnouncements = [];
    this.totalData = 0;
    this.tableData = [];
    this.dataSource.data = [];
  }

  paginateTable(resetPage: boolean): void {
    if (resetPage) this.pageIndex = 0;
    else this.pageIndex = this.paginator.pageIndex;
    this.pageSize = this.paginator.pageSize;
    this.applyPagination();
  }

  openModal(editData?: IModalEmitData): void {
    const modalRef = this.dialog.open(AddGlobalAnnoucementsModalComponent, {
      data: editData ? editData : undefined,
    });

    modalRef.afterClosed().subscribe((success: any) => {
      if (success) {
        this.loadAnnouncements();
      }
    });
  }

  deleteModal(event: IModalEmitData): void {
    const row = event.data;
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      data: {
        data: {
          modalInfo: {
            title: this.translocoService.translate('configuration.globalAnnouncements.deleteTitle'),
            message: this.translocoService.translate('configuration.globalAnnouncements.deleteMessage'),
            primaryBtnClass: 'red',
            showCancel: true,
            primaryBtnText: Modal.Delete,
            secondaryBtnText: this.translocoService.translate('configuration.common.cancel')
          }
        }
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.subscriptions.add(
          this.announcementService.delete(row.id)
            .pipe(take(1))
            .subscribe(() => {
              this.loadAnnouncements();
            })
        );
      }
    });
  }

  onAllStatesChange(): void {
    this.searchAllStates = !this.searchAllStates;
    if(this.searchAllStates) {
      this.searchStateId = null;
      this.clearDropdowns = !this.clearDropdowns;
    }else {
      this.clearDropdowns = false;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
